import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, X, Search } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import Breadcrumb from '../components/Breadcrumb';
import Pagination from '../components/Pagination';
import { ProductCardSkeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { repository, COLLECTIONS, resolveSlug } from '../lib/repository';

const SORTS = [
  { value: 'featured', label: 'Featured' },
  { value: 'newest', label: 'New In' },
  { value: 'rating', label: 'Rating' },
  { value: 'price-asc', label: 'Price ↑' },
  { value: 'price-desc', label: 'Price ↓' },
];

const PAGE_SIZE = 12;

export default function Shop() {
  const { category: routeParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const resolved = resolveSlug(routeParam);

  const [all, setAll] = useState(null); // null = loading
  const [sort, setSort] = useState('featured');
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [filterOpen, setFilterOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setAll(null);
    setPage(1);
    setSelectedSizes([]);
    setSelectedColors([]);
    setQuery(searchParams.get('q') || '');
    const args = {};
    if (resolved.kind === 'department') args.department = resolved.slug;
    if (resolved.kind === 'category') args.category = resolved.slug;
    if (resolved.kind === 'collection') args.collection = resolved.slug;
    repository.listProducts(args).then(setAll);
    repository.listCategories().then(setCategories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParam]);

  // Meta info for the hero
  let title = 'Everything';
  let tagline = 'Considered pieces across the house.';
  let eyebrow = 'The Catalog';
  if (resolved.kind === 'collection') {
    title = COLLECTIONS[resolved.slug].name;
    tagline = COLLECTIONS[resolved.slug].tagline;
    eyebrow = 'Curated';
  } else if (resolved.kind === 'department') {
    title = resolved.slug.charAt(0).toUpperCase() + resolved.slug.slice(1);
    tagline = `Considered pieces for ${title.toLowerCase()}.`;
    eyebrow = 'Department';
  } else if (resolved.kind === 'category') {
    const cat = categories.find((c) => c.slug === resolved.slug);
    if (cat) { title = cat.name; tagline = cat.tagline; eyebrow = 'Category'; }
  }

  const filtered = useMemo(() => {
    if (!all) return [];
    let items = [...all];
    if (query.trim()) {
      const q = query.toLowerCase();
      items = items.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.tags.some((t) => t.includes(q))
      );
    }
    if (selectedSizes.length) items = items.filter((p) => p.sizes.some((s) => selectedSizes.includes(s)));
    if (selectedColors.length) items = items.filter((p) => p.colors.some((c) => selectedColors.includes(c.name)));
    const priceOf = (p) => p.salePrice || p.price;
    if (sort === 'price-asc')  items.sort((a, b) => priceOf(a) - priceOf(b));
    if (sort === 'price-desc') items.sort((a, b) => priceOf(b) - priceOf(a));
    if (sort === 'newest')     items.sort((a, b) => (b.newIn ? 1 : 0) - (a.newIn ? 1 : 0));
    if (sort === 'rating')     items.sort((a, b) => b.rating - a.rating);
    return items;
  }, [all, sort, selectedSizes, selectedColors, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [sort, selectedSizes, selectedColors, query]);

  const allSizes = useMemo(() => Array.from(new Set((all || []).flatMap((p) => p.sizes))), [all]);
  const allColors = useMemo(() => {
    const map = new Map();
    (all || []).forEach((p) => p.colors.forEach((c) => map.set(c.name, c.hex)));
    return Array.from(map, ([name, hex]) => ({ name, hex }));
  }, [all]);

  const toggle = (setter) => (value) => setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const departments = useMemo(() => {
    const set = new Set(categories.map((c) => c.department));
    return Array.from(set);
  }, [categories]);

  const breadcrumb = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Shop' },
    ...(routeParam ? [{ label: title }] : []),
  ];

  const testid = `page-shop-${routeParam || 'all'}`;

  return (
    <div data-testid={testid}>
      <SEO
        title={title === 'Everything' ? 'Shop All' : `Shop ${title}`}
        description={tagline}
        path={routeParam ? `/shop/${routeParam}` : '/shop'}
        breadcrumb={breadcrumb}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: title,
          description: tagline,
          url: `https://evermark.com/shop${routeParam ? '/' + routeParam : ''}`,
        }}
      />
      {/* Hero band */}
      <section className="border-b border-border">
        <div className="container py-16 md:py-24">
          <Breadcrumb items={breadcrumb} className="mb-8" />
          <p className="overline text-muted-foreground">{eyebrow}</p>
          <h1 className="mt-4 font-display text-6xl md:text-8xl leading-[0.9] tracking-tight capitalize">
            {title}
          </h1>
          <p className="mt-4 text-muted-foreground max-w-xl">{tagline}</p>

          {/* Department tabs */}
          <nav className="mt-8 flex flex-wrap gap-x-6 gap-y-3 overline" aria-label="Filter by department">
            <Link to="/shop" className={`link-underline ${resolved.kind === 'all' ? 'opacity-100' : 'opacity-60'}`} data-testid="shop-filter-all">All</Link>
            {departments.map((d) => (
              <Link key={d} to={`/shop/${d}`} data-testid={`shop-filter-${d}`} className={`link-underline capitalize ${resolved.slug === d ? 'opacity-100' : 'opacity-60'}`}>
                {d}
              </Link>
            ))}
            <span className="opacity-30">·</span>
            {Object.entries(COLLECTIONS).map(([slug, meta]) => (
              <Link key={slug} to={`/shop/${slug}`} data-testid={`shop-filter-${slug}`} className={`link-underline ${resolved.slug === slug ? 'opacity-100' : 'opacity-60'}`}>
                {meta.name}
              </Link>
            ))}
          </nav>

          {/* Sub-category chips when browsing a department */}
          {resolved.kind === 'department' && (
            <div className="mt-6 flex flex-wrap gap-2">
              {categories.filter((c) => c.department === resolved.slug).map((c) => (
                <Link
                  key={c.slug}
                  to={`/shop/${c.slug}`}
                  data-testid={`shop-subcat-${c.slug}`}
                  className="text-xs px-3 py-1.5 border border-border hover:border-foreground transition-colors duration-500"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="container py-6 border-b border-border sticky top-16 md:top-28 z-30 bg-background/85 backdrop-blur-xl -mx-6 md:-mx-12 px-6 md:px-12 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <div className="flex items-center gap-3 md:min-w-[280px] flex-1 border-b border-foreground md:border-none md:border-l md:pl-4">
          <Search className="h-4 w-4 opacity-60" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search within results…"
            data-testid="shop-inline-search"
            className="flex-1 bg-transparent outline-none py-2 text-sm placeholder:text-muted-foreground/60"
          />
          {query && (
            <button aria-label="Clear search" onClick={() => setQuery('')} className="opacity-60 hover:opacity-100" data-testid="shop-inline-search-clear">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between md:gap-8">
          <button
            type="button"
            onClick={() => setFilterOpen((o) => !o)}
            data-testid="shop-filter-toggle"
            className="overline inline-flex items-center gap-2 link-underline"
          >
            <Filter className="h-3 w-3" /> Filter {(selectedSizes.length + selectedColors.length) > 0 && `(${selectedSizes.length + selectedColors.length})`}
          </button>
          <p className="overline text-muted-foreground tabular" data-testid="shop-result-count">
            {all === null ? '…' : `${filtered.length} pieces`}
          </p>
          <div className="flex items-center gap-3">
            <label className="overline text-muted-foreground" htmlFor="sort">Sort</label>
            <select
              id="sort"
              data-testid="shop-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent border-b border-foreground text-sm py-1 pr-6 focus:outline-none"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value} className="bg-background text-foreground">{s.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="container py-12 grid gap-12 md:grid-cols-[240px_1fr]">
        {/* Filters */}
        <aside className={`${filterOpen ? 'block' : 'hidden md:block'} space-y-10`} data-testid="shop-filters">
          <div>
            <p className="overline mb-4">Size</p>
            <div className="flex flex-wrap gap-2">
              {allSizes.map((s) => (
                <button
                  key={s}
                  onClick={() => toggle(setSelectedSizes)(s)}
                  data-testid={`filter-size-${s}`}
                  className={`h-9 min-w-11 px-3 text-xs tabular border transition-colors duration-500 ${selectedSizes.includes(s) ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="overline mb-4">Colour</p>
            <ul className="space-y-3 max-h-64 overflow-y-auto pr-2">
              {allColors.map((c) => (
                <li key={c.name}>
                  <button
                    onClick={() => toggle(setSelectedColors)(c.name)}
                    data-testid={`filter-color-${c.name}`}
                    className={`flex items-center gap-3 w-full text-left text-sm transition-opacity duration-500 ${selectedColors.includes(c.name) ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
                  >
                    <span className="h-4 w-4 rounded-full border border-border" style={{ background: c.hex }} />
                    <span className="link-underline">{c.name}</span>
                    {selectedColors.includes(c.name) && <X className="h-3 w-3 ml-auto" />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {(selectedSizes.length + selectedColors.length + (query ? 1 : 0)) > 0 && (
            <Button variant="ghost" onClick={() => { setSelectedSizes([]); setSelectedColors([]); setQuery(''); }} data-testid="filter-clear">
              Clear filters
            </Button>
          )}
        </aside>

        {/* Grid */}
        <div>
          {all === null ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-12 md:gap-x-6" data-testid="shop-loading">
              {Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              eyebrow="Nothing matches"
              title="No pieces yet — try widening the filter."
              description="Clear a size, colour or search term to see more."
              action={
                <Button variant="outline" onClick={() => { setSelectedSizes([]); setSelectedColors([]); setQuery(''); }} data-testid="shop-empty-clear">
                  Clear filters
                </Button>
              }
              testId="shop-empty"
            />
          ) : (
            <>
              <motion.div layout className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-12 md:gap-x-6">
                {pageItems.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </motion.div>
              <Pagination page={page} totalPages={totalPages} onPageChange={(n) => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
